import { useBackendApi } from './backendApi';
import { useEventsPublisher } from 'event-bus';
import { OAuthProviderType } from './oAuthIdentityService';
import { BadRequestError, ResourceNotFoundError } from 'request-sender';

export interface OAuthLink {
  id: number;
  provider: OAuthProviderType;
  email: string;
  firstName: string;
  lastName: string;
  externalUserId: string;
  createdAt: string;
}

export interface LinkExistingAccountRequest {
  code: string;
}

export interface LinkExistingAccountResponse {
  token: string;
}

export interface DeleteLinkResponse {
  success: boolean;
}

export class OAuthAccountAlreadyLinkedError extends Error {
  constructor() {
    super('This OAuth account is already linked to another user account');
    Object.setPrototypeOf(this, OAuthAccountAlreadyLinkedError.prototype);
  }
}

export class ProviderAlreadyLinkedError extends Error {
  constructor() {
    super('This provider is already linked to your account');
    Object.setPrototypeOf(this, ProviderAlreadyLinkedError.prototype);
  }
}

export class EmailMismatchError extends Error {
  private _expectedEmail: string;
  private _providedEmail: string;

  constructor(expectedEmail: string, providedEmail: string) {
    super('The OAuth account email does not match your current account email');
    this._expectedEmail = expectedEmail;
    this._providedEmail = providedEmail;
    Object.setPrototypeOf(this, EmailMismatchError.prototype);
  }

  get expectedEmail(): string {
    return this._expectedEmail;
  }

  get providedEmail(): string {
    return this._providedEmail;
  }
}

export class InvalidOAuthProviderError extends Error {
  private _provider: string;
  private _supportedProviders: string[];

  constructor(provider: string, supportedProviders: string[] = ['google', 'apple']) {
    super('Invalid OAuth provider specified');
    this._provider = provider;
    this._supportedProviders = supportedProviders;
    Object.setPrototypeOf(this, InvalidOAuthProviderError.prototype);
  }

  get provider(): string {
    return this._provider;
  }

  get supportedProviders(): string[] {
    return this._supportedProviders;
  }
}

export class OAuthLinkNotFoundError extends Error {
  private _linkId: number;

  constructor(linkId: number) {
    super('OAuth link not found or does not belong to current user');
    this._linkId = linkId;
    Object.setPrototypeOf(this, OAuthLinkNotFoundError.prototype);
  }

  get linkId(): number {
    return this._linkId;
  }
}

export const OAUTH_LINKS_UPDATED = 'OAUTH_LINKS_UPDATED';

export function useOAuthLinkService() {
  const nekstApi = useBackendApi();
  const eventsPublisher = useEventsPublisher();

  const getLinks = async (): Promise<OAuthLink[]> => {
    return await nekstApi.get('/settings/oauth') as OAuthLink[];
  };

  const getOAuthUrl = async (provider: OAuthProviderType): Promise<string> => {
    const response = await nekstApi.get(`/settings/oauth/${provider}/url`) as { url: string };
    return response.url;
  }

  const deleteLink = async (linkId: number): Promise<void> => {
    try {
      await nekstApi.delete(`/settings/oauth/${linkId}`) as DeleteLinkResponse;
      eventsPublisher.publish(OAUTH_LINKS_UPDATED, {});
    } catch (e) {
      if (e instanceof ResourceNotFoundError) {
        throw new OAuthLinkNotFoundError(linkId);
      }
      throw e;
    }
  };

  const linkExistingAccount = async (
    provider: OAuthProviderType,
    request: LinkExistingAccountRequest
  ): Promise<string> => {
    try {
      const response = await nekstApi.post(`/settings/oauth/${provider}/link`, request) as LinkExistingAccountResponse;
      eventsPublisher.publish(OAUTH_LINKS_UPDATED, {});
      return response.token;
    } catch (e) {
      if (e instanceof BadRequestError) {
        const errorCode = e.getErrorCode();

        switch (errorCode) {
          case 'OAUTH_ACCOUNT_ALREADY_LINKED':
            throw new OAuthAccountAlreadyLinkedError();
          case 'PROVIDER_ALREADY_LINKED':
            throw new ProviderAlreadyLinkedError();
          case 'EMAIL_MISMATCH':
            throw new EmailMismatchError(
              e.getResponseField('expectedEmail', ''),
              e.getResponseField('providedEmail', '')
            );
          case 'INVALID_OAUTH_PROVIDER':
            throw new InvalidOAuthProviderError(
              e.getResponseField('provider', provider),
              e.getResponseField('supportedProviders', ['google', 'apple'])
            );
        }
      }
      throw e;
    }
  };

  return {
    getLinks,
    getOAuthUrl,
    deleteLink,
    linkExistingAccount,
  };
}
